// Generado desde design-system/components/overlays/Menu.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { cloneElement, forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Kbd, ariaShortcut } from '../controls/Kbd';
import { EXIT_MS, FloatingLayer, getActiveElement, mergeRefs, motionStyle, originFor, useControllable, useDismiss, usePresence, useReducedMotion, } from './primitives';
const hasChecks = (items) => items.some((it) => (it.type === 'section' ? hasChecks(it.items || []) : it.type !== 'separator' && it.checked !== undefined));
/**
 * MenuList — lista de acciones con role="menu" sobre vidrio fuerte.
 * Teclado: ↑ ↓ (cíclico), Inicio/Fin, búsqueda por letra, Enter/Espacio ejecuta, Tab/Esc cierra.
 * El puntero mueve el foco (resaltado único). Se usa dentro de Menu, ContextMenu, SplitButton y ExportButton.
 */
export const MenuList = forwardRef(function MenuList({ items = [], onAction, onClose, label, labelledBy, autoFocus = true, focusLast = false, className, style, id, minWidth = 248 }, ref) {
    const listRef = useRef(null);
    const uid = useId();
    const checks = hasChecks(items);
    const typeahead = useRef({ buffer: '', time: 0 });
    const enabled = () => [...(listRef.current?.querySelectorAll('[data-menuitem]:not([aria-disabled="true"])') ?? [])];
    useEffect(() => {
        if (!autoFocus)
            return undefined;
        const raf = requestAnimationFrame(() => {
            const els = enabled();
            (focusLast ? els[els.length - 1] : els[0])?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(raf);
    }, [autoFocus, focusLast]);
    const onKeyDown = (e) => {
        const els = enabled();
        if (!els.length)
            return;
        const i = els.indexOf(getActiveElement(e.currentTarget));
        const go = (n) => {
            e.preventDefault();
            els[(n + els.length) % els.length]?.focus({ preventScroll: true });
        };
        if (e.key === 'ArrowDown')
            go(i + 1);
        else if (e.key === 'ArrowUp')
            go(i < 0 ? els.length - 1 : i - 1);
        else if (e.key === 'Home')
            go(0);
        else if (e.key === 'End')
            go(els.length - 1);
        else if (e.key === 'Tab') {
            e.preventDefault();
            onClose?.('tab');
        }
        else if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const now = Date.now();
            const t = typeahead.current;
            t.buffer = now - t.time > 600 ? e.key.toLowerCase() : t.buffer + e.key.toLowerCase();
            t.time = now;
            const ordered = [...els.slice(i + 1), ...els.slice(0, i + 1)];
            const hit = ordered.find((el) => (el.dataset.label || '').toLowerCase().startsWith(t.buffer));
            if (hit)
                hit.focus({ preventScroll: true });
        }
    };
    const renderItem = (it, key) => {
        const Icon = it.icon;
        const role = it.checked !== undefined ? 'menuitemcheckbox' : 'menuitem';
        const danger = it.tone === 'danger';
        return (<button key={it.id ?? key} type="button" role={role} tabIndex={-1} data-menuitem="" data-label={it.label} aria-checked={role === 'menuitemcheckbox' ? !!it.checked : undefined} aria-disabled={it.disabled || undefined} aria-keyshortcuts={it.shortcut ? ariaShortcut(it.shortcut) : undefined} onClick={() => !it.disabled && onAction?.(it)} onPointerMove={(e) => {
                if (!it.disabled && getActiveElement(e.currentTarget) !== e.currentTarget)
                    e.currentTarget.focus({ preventScroll: true });
            }} className={cn('group flex min-h-[40px] w-full items-center gap-3 rounded-sm px-2.5 py-2 text-left text-body-sm font-semibold outline-none', 'transition-colors duration-fast ease-out-ios', 'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--db-ring)_70%,transparent)]', danger ? 'text-neg focus:bg-neg-soft' : 'text-ink focus:bg-accent-soft', it.disabled && 'cursor-not-allowed opacity-45')}>
        {checks && (<span className="grid w-4 shrink-0 place-items-center text-accent-ink" aria-hidden="true">
            {it.checked ? <Check size={15} strokeWidth={2.6}/> : null}
          </span>)}
        {Icon && (<Icon size={17} strokeWidth={1.9} aria-hidden="true" className={cn('shrink-0 transition-colors duration-fast', danger ? 'text-neg' : 'text-ink-3 group-focus:text-accent-ink')}/>)}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{it.label}</span>
          {it.description && <span className="block truncate text-caption font-normal text-ink-3">{it.description}</span>}
        </span>
        {it.meta && <span className="shrink-0 text-caption font-normal tabular-nums text-ink-3">{it.meta}</span>}
        {it.shortcut && <Kbd combo={it.shortcut} className="shrink-0"/>}
      </button>);
    };
    return (<div ref={mergeRefs(ref, listRef)} id={id} role="menu" aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy} aria-orientation="vertical" onKeyDown={onKeyDown} className={cn('db-glass-strong flex flex-col rounded-md p-1.5 text-ink outline-none', className)} style={{ minWidth, ...style }}>
      {items.map((it, idx) => {
            if (it.type === 'separator')
                return <div key={`sep-${idx}`} role="separator" className="mx-2 my-1.5 h-px bg-hair"/>;
            if (it.type === 'section') {
                const hid = `${uid}-s${idx}`;
                return (<div key={`sec-${idx}`} role="group" aria-labelledby={hid} className={cn(idx > 0 && 'mt-1.5 border-t border-hair pt-1.5')}>
              <div id={hid} className="px-2.5 pb-1 pt-1.5 font-display text-eyebrow uppercase text-ink-3">
                {it.label}
              </div>
              {(it.items || []).map((sub, j) => renderItem(sub, `${idx}-${j}`))}
            </div>);
            }
            return renderItem(it, idx);
        })}
    </div>);
});
/**
 * Menu — menú desplegable (patrón menu button). El `trigger` es un elemento (Button/IconButton)
 * al que se le inyectan aria-haspopup, aria-expanded y los manejadores.
 * inline: la capa se posiciona dentro del contenedor (vistas previas, paneles); por defecto va en portal.
 */
export function Menu({ trigger, items, label, onAction, placement = 'bottom-start', inline = false, open: openProp, defaultOpen = false, onOpenChange, autoFocus = true, dismissable = true, minWidth, className, menuClassName, }) {
    const [open, setOpen] = useControllable(openProp, defaultOpen, onOpenChange);
    const [focusLast, setFocusLast] = useState(false);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const menuId = useId();
    const reduced = useReducedMotion();
    const { mounted, visible } = usePresence(open, EXIT_MS.pop);
    const close = (returnFocus) => {
        setOpen(false);
        if (returnFocus)
            triggerRef.current?.focus({ preventScroll: true });
    };
    useDismiss({ open, onClose: dismissable ? (why) => close(why === 'escape') : undefined, refs: [triggerRef, menuRef] });
    const t = trigger;
    const triggerProps = {
        ref: mergeRefs(triggerRef, t.ref),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: (e) => {
            t.props.onClick?.(e);
            setFocusLast(false);
            setOpen(!open);
        },
        onKeyDown: (e) => {
            t.props.onKeyDown?.(e);
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusLast(e.key === 'ArrowUp');
                setOpen(true);
            }
        },
    };
    const triggerEl = cloneElement(t, triggerProps);
    return (<span className={cn('relative inline-flex', className)}>
      {triggerEl}
      {mounted && (<FloatingLayer inline={inline} anchorRef={triggerRef} placement={placement}>
          {({ placement: p }) => (<MenuList ref={menuRef} id={menuId} items={items} label={label} minWidth={minWidth} autoFocus={autoFocus && open} focusLast={focusLast} className={menuClassName} style={motionStyle('pop', visible, reduced, originFor(p))} onClose={() => close(true)} onAction={(item) => {
                    item.onSelect?.(item);
                    onAction?.(item);
                    if (!item.keepOpen)
                        close(true);
                }}/>)}
        </FloatingLayer>)}
    </span>);
}
/**
 * ContextMenu — menú contextual sobre un área (clic derecho, Mayús+F10 o tecla Menú).
 * onOpen(event) permite saber sobre qué fila/elemento se abrió.
 * defaultOpen + defaultPoint lo muestran abierto (miniaturas, documentación).
 */
export function ContextMenu({ items, children, label = 'Acciones', onAction, onOpen, inline = false, defaultOpen = false, defaultPoint, autoFocus = true, dismissable = true, className, as: Tag = 'div', }) {
    const [point, setPoint] = useState(defaultOpen ? defaultPoint ?? { x: 24, y: 24 } : null);
    const [userOpened, setUserOpened] = useState(false);
    const areaRef = useRef(null);
    const menuRef = useRef(null);
    const returnRef = useRef(null);
    const reduced = useReducedMotion();
    const open = !!point;
    const { mounted, visible } = usePresence(open, EXIT_MS.pop);
    const [shown, setShown] = useState(point);
    useEffect(() => {
        if (point)
            setShown(point);
    }, [point]);
    const close = (returnFocus) => {
        setPoint(null);
        if (returnFocus)
            returnRef.current?.focus?.({ preventScroll: true });
    };
    useDismiss({ open, onClose: dismissable ? (why) => close(why === 'escape') : undefined, refs: [menuRef] });
    const openAt = (clientX, clientY, e) => {
        returnRef.current = getActiveElement(e.currentTarget);
        onOpen?.(e);
        setUserOpened(true);
        if (inline) {
            const r = areaRef.current.getBoundingClientRect();
            setPoint({ x: clientX - r.left, y: clientY - r.top });
        }
        else
            setPoint({ x: clientX, y: clientY });
    };
    // En línea: evita que el menú se salga del área (medidas sin escala).
    useLayoutEffect(() => {
        if (!inline || !mounted || !menuRef.current || !areaRef.current || !shown)
            return;
        const m = menuRef.current;
        const area = areaRef.current;
        const maxX = area.clientWidth - m.offsetWidth - 8;
        const maxY = area.clientHeight - m.offsetHeight - 8;
        if (shown.x > maxX || shown.y > maxY)
            setShown({ x: Math.max(8, Math.min(shown.x, maxX)), y: Math.max(8, Math.min(shown.y, maxY)) });
    }, [inline, mounted, shown]);
    return (<Tag ref={areaRef} className={cn('relative', className)} onContextMenu={(e) => {
            e.preventDefault();
            openAt(e.clientX, e.clientY, e);
        }} onKeyDown={(e) => {
            if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
                e.preventDefault();
                const r = e.target.getBoundingClientRect();
                openAt(r.left + 16, r.top + r.height / 2, e);
            }
        }}>
      {children}
      {mounted && shown && (<FloatingLayer inline={inline} point={shown} placement="bottom-start" offset={2}>
          <MenuList ref={menuRef} items={items} label={label} autoFocus={autoFocus && open && userOpened} style={motionStyle('pop', visible, reduced, 'left top')} onClose={() => close(true)} onAction={(item) => {
                item.onSelect?.(item);
                onAction?.(item);
                if (!item.keepOpen)
                    close(true);
            }}/>
        </FloatingLayer>)}
    </Tag>);
}
